import { cloneDeep, isEqual } from "lodash";

/**
 *
 * @param collectionPages
 * create a new collection of pages
 * if pages contain dynamic content, create all possible instances of how dynamic content can be displayed
 * make a new copy of each page for each instance of dynamic content and then this instance will be used to dictate how modules should be rendered
 */
export function generateFlatPages(collectionPages) {
  let flatPages = [];
  for (let page of collectionPages) {
    const dynamicModules = page.modules.filter(
      (module) =>
        module.type === "carousel" ||
        module.type === "tabbed-content" ||
        module.type === "hotspots" ||
        module.type === "config-tool"
    );
    if (dynamicModules.length > 0) {
      let instances = {};
      for (const module of dynamicModules) {
        instances[module.type] = moduleInstanceFnMap[module.type](module);
      }
      for (const key of Object.keys(instances)) {
        const isConfig = key === "config-tool";
        const numInstances = !isConfig ? instances[key] : instances[key].length;
        //make a copy of a page for each instance
        for (let i = 0; i < numInstances; i++) {
          let pageCopy = Object.assign({}, page);
          pageCopy.instance = !isConfig
            ? { module: key, index: i }
            : { module: key, index: i, config: instances[key][i] };
          flatPages.push(pageCopy);
        }
      }
    } else flatPages.push(page);
  }
  return flatPages;
}

// execute appropriate function based on module type
const moduleInstanceFnMap = {
  carousel: (module) => module.content.slides.length,
  "tabbed-content": (module) => module.content.slides.length,
  hotspots: (module) => module.content.hotspots.length,
  "config-tool": (module) => {
    //with the new set up, config objects match the layout of formulas
    //so map them to serve as pre-made configs for the tool to use
    const PRIMARY_FILTER = "printer-model";
    const { content } = module;
    const { settings } = content;
    return [{}].concat(
      settings
        .map(({ formula: f }) => f)
        .sort((a, b) => a[PRIMARY_FILTER] - b[PRIMARY_FILTER])
    );
  }
};

//checks whether a built config and setting formula are the same
/**checks
 * number of keys
 * same keys
 * same values
 */
export function sameSettings(a, b) {
  if (!a || !b) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (!isEqual(aKeys.sort(), bKeys.sort())) return false;
  for (const k of aKeys) {
    if (typeof a[k] === "object") {
      //check additional options
      if (!sameSettings(a[k], b[k])) return false;
      // eslint-disable-next-line
    } else if (a[k] != b[k]) return false;
  }
  return true;
}

//maps each product ID to its product_group key
//94: "printer-model"
const productOptionIdMap = (product_groups) => {
  const obj = {};
  product_groups.forEach((f) => {
    f.products_in_group.forEach(({ id }) => {
      obj[id] = f.key;
    });
  });
  return obj;
};

//returns an array of all the product IDs in a built config
//{printer-model:94, additional-options:{98:1}} => [94,98]
export const getConfigProductIds = (config, useK) => {
  if (!config) return;
  let arr = [];
  for (const [k, v] of Object.entries(config)) {
    if (typeof v === "object") {
      arr = arr.concat(getConfigProductIds(v, true));
    } else arr.push(Number.parseInt(useK ? k : v));
  }
  return arr;
};

//determines if a config satisfies all the rules specified by one product's rules
const passRules = (rulesObj, config, product_groups) => {
  if (!rulesObj) return true;
  const { rules, meetAll } = rulesObj;
  if (!rules || rules.length === 0) return true;
  const passRule = (rule) => {
    const { operator, product, value_1: v1, value_2: v2 } = rule;
    if (!product) return true;
    const pid = product?.id;
    //productOptionIdMap is mapping pid to keys in the config to look up its value in the config
    const pval = config?.[productOptionIdMap(product_groups)[pid]]?.[pid] || 0;
    switch (operator) {
      case "between":
        return v1 <= pval && pval <= v2;
      case "equals":
        return pval === v1;
      case "not_equals":
        return pval !== v1;
      default:
        return pval === v1;
    }
  };
  //put all the rule results into a set
  const evaluatedRules = new Set(rules.map((r) => passRule(r)));
  if (meetAll === true)
    //if only value in set is true, then all rules were met
    //else return false
    return evaluatedRules.has(true) && evaluatedRules.size === 1;
  //if !meetAll then just having any rule result be true is enough
  return evaluatedRules.has(true);
};

//gets the rules for a given product id
const getRules = (primaryProduct, optValue) => {
  if (!primaryProduct) return null;
  const optProduct = primaryProduct?.related_products?.find(
    (e) => e.id === optValue
  );
  return {
    rules: optProduct?.product_related_product_rules,
    meetAll: optProduct?.all_conditions_must_be_met
  };
};

//takes a config and sees if all the rules are satisfied within it
export const allRulesSatisfied = (product_groups, primaryProduct, config) => {
  if (!primaryProduct || !config) return false;
  const configKeys = getConfigProductIds(config, false);
  for (const k of configKeys) {
    const rulesObj = getRules(primaryProduct, k);
    const passedRule = passRules(rulesObj, config, product_groups);
    if (!passedRule) return false;
  }
  return true;
};

//get the ids of products whose inclusion is allowed within the config
const getCompliers = (product_groups, primaryProduct, config) => {
  const configKeys = getConfigProductIds(config, false);
  return new Set(
    configKeys?.filter((e) =>
      passRules(getRules(primaryProduct, e), config, product_groups)
    ) || []
  );
};

/**CORNER CASE
 * user selects stapler
 * user then selects staple cartridge
 * both are now in the config
 * user deselects stapler (it's removed from config)
 * staple cartridge still there though!
 * this removes any lingering products whose inclusion in the config breaks a rule
 */
export const removeRuleBreakers = (
  product_groups,
  primaryProduct,
  config,
  compliers,
  //when doing additional-options section, since those are object, useK says use the key as what to see if it's a complier
  useK
) => {
  if (!primaryProduct) return config;
  if (!compliers)
    compliers = getCompliers(product_groups, primaryProduct, config);
  const obj = {};
  for (const [k, v] of Object.entries(config)) {
    if (typeof v === "object") {
      obj[k] = removeRuleBreakers(
        product_groups,
        primaryProduct,
        v,
        compliers,
        true
      );
    }
    if (compliers.has(useK ? Number.parseInt(k) : v)) obj[k] = v;
  }
  return obj;
};

//adds a value to a conig
export const getHypotheticalConfig = (builtConfig, dataFilter, value, key) => {
  if (!builtConfig) return null;
  const config = cloneDeep(builtConfig);
  if (key) {
    if (config[dataFilter]) config[dataFilter][key] = value;
    else config[dataFilter] = { [key]: value };
  } else config[dataFilter] = value;
  return config;
};

//checks if config with new value satisfies all rules
export const hypotheticalConfigSatisfiesRules = (
  product_groups,
  primaryProduct,
  builtConfig,
  dataFilter,
  value,
  key
) => {
  const config = getHypotheticalConfig(builtConfig, dataFilter, value, key);
  const satisfiesRules = allRulesSatisfied(
    product_groups,
    primaryProduct,
    config
  );
  return satisfiesRules;
};

//removes elements from config that are 0 or empty
export const filterConfig = (config) => {
  const obj = {};
  for (const [k, v] of Object.entries(config)) {
    if (typeof v === "object") {
      const vo = filterConfig(v);
      if (Object.keys(vo).length > 0) obj[k] = vo;
    } else if (v > 0) obj[k] = v;
  }
  return obj;
};

//filters out the additional options that do not impact dimensions
export function filterByDimensionImpact(config, primaryProduct) {
  const dimensionImpactMap = Object.fromEntries(
    (primaryProduct?.related_products || []).map((e) => [
      e.id,
      e.impacts_dimensions
    ])
  );
  const obj = {};
  for (const [k, v] of Object.entries(config)) {
    if (typeof v === "object") {
      obj[k] = Object.fromEntries(
        Object.entries(v).filter((e) => dimensionImpactMap[e[0]])
      );
    } else obj[k] = v;
  }
  return filterConfig(obj);
}

export function uniqueArrByObjKey(arr, key) {
  return [...new Map(arr.map((item) => [item[key], item])).values()];
}

//returns all related products in the form productName:{id: {allInfo}, id:{allInfo}}
//for when two related products have the same name but are different
export function getRelatedProductsMap(productGroup) {
  if (!productGroup) return {};
  const allRelatedProductsArr = productGroup?.flatMap((p) =>
    p.related_products.map((rp) => {
      const {
        limit_per_unit: limit,
        product_related_product_rules: rules,
        impacts_dimensions: impactDimensions,
        all_conditions_must_be_met: meetAll,
        name,
        model,
        sku,
        id
      } = rp;
      return {
        id,
        limit,
        rules: { rules, meetAll },
        impactDimensions,
        name: name || model,
        label: name || model,
        value: id,
        sku,
        relatedTo: [p.id]
      };
    })
  );
  const obj = {};
  for (const e of allRelatedProductsArr) {
    const { id, name } = e;
    if (obj[name]) {
      if (obj[name][id]) {
        obj[name][id].relatedTo.push(e.relatedTo[0]);
      } else obj[name] = { ...obj[name], [id]: e };
    } else obj[name] = { [id]: e };
  }
  return obj;
}

//only keep options with unique names
// if some objects share the same name, take the one that is related to the primary
//if none are related to the primary product, just take any one of them to display (won't matter which since it'll be disabled anyway)
export function getUniqueOptions(optionGroup, primaryId) {
  const uniqueOptions = [];
  const seenNames = new Set();
  for (const o of optionGroup) {
    if (!seenNames.has(o.name)) {
      const optionsWithSameName = optionGroup.filter((e) => e.name === o.name);
      const relatedOption = optionsWithSameName.find((e) =>
        e.relatedTo.includes(primaryId)
      );
      uniqueOptions.push(relatedOption || optionsWithSameName[0]);
      seenNames.add(o.name);
    }
  }
  return uniqueOptions;
}
